import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { forwardRef } from 'react';

import typography from 'storybookConfig/mixins/typography';

/** 44px, the height of a button, so a field and its button line up in one row. */
export const INPUT_HEIGHT = 44;

export enum InputVariants {
  Gray = 'gray',
  White = 'white',
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The input's variant
   */
  variant?: `${InputVariants}`;
  /**
   * Should the input fill its container
   */
  isFullWidth?: boolean;
  /**
   * Should the input remove border on focus
   */
  removeBorder?: boolean;
}

/**
 * A basic text input.
 */

const BaseInput = forwardRef<HTMLInputElement, InputProps>(
  ({ isFullWidth, type = 'text', ...props }, ref) => <input ref={ref} type={type} {...props} />
);

const Input = styled(BaseInput, {
  shouldForwardProp: (prop) => !['variant', 'isFullWidth', 'removeBorder'].includes(prop),
})<InputProps>`
  outline: 0;
  max-width: 100%;
  min-height: ${INPUT_HEIGHT}px;
  border-radius: ${({ theme }) => theme.radius.field};
  padding: 10px 16px;
  border: 1px solid ${({ theme }) => theme.color.inputBorder};

  ${({ isFullWidth }) =>
    isFullWidth &&
    css`
      width: 100%;
      max-width: 100%;
    `}

  ${({ theme, variant = InputVariants.Gray }) => {
    switch (variant) {
      case InputVariants.Gray:
        return css`
          background: ${theme.color.inputBackground};
        `;
      case InputVariants.White:
        return css`
          background: ${theme.color.surfaceCanvas};
        `;
      default:
        return css``;
    }
  }}

  ${({ theme, disabled, readOnly }) =>
    disabled || readOnly
      ? css`
          cursor: not-allowed;
          color: ${disabled ? theme.color.inputTextDisabled : theme.color.bodyTextSecondary};
          ${typography('Inputs/Input')};
        `
      : css`
          color: ${theme.color.bodyTextPrimary};
          ${typography('Inputs/Input')};
        `};

  &::placeholder {
    color: ${({ theme, disabled }) =>
      disabled ? theme.color.inputTextDisabled : theme.color.bodyTextSecondary};
  }

  // When the input is active or focused
  &:active,
  &:focus {
    border-color: ${({ theme }) => theme.color.inputBorderFocus};
  }

  // When the input is invalid, natively or as marked by its form
  &:invalid,
  &[aria-invalid='true'] {
    border-color: ${({ theme }) => theme.color.errorBase};
  }

  // When disabled
  &:disabled {
    background: ${({ theme }) => theme.color.inputBackgroundDisabled};
    border-color: ${({ theme }) => theme.color.inputBorderDisabled} !important;
  }
`;

export default Input;
